import React, { useState, useEffect } from 'react';
import { getUsersWithRoles, updateUser, createUser, getRoles } from '../../services/user.service';
import { showSuccess, showError } from '../../services/notification.service';
import './UserManagement.css';

function UserManagement() {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [editUser, setEditUser] = useState(null);
  const [editFields, setEditFields] = useState({});
  const [saving, setSaving] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [createFields, setCreateFields] = useState({ name: '', email: '', phone: '', rbacId: '' });
  const [creating, setCreating] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      const [data, roleList] = await Promise.all([getUsersWithRoles(), getRoles()]);
      setUsers(data);
      const safeRoleList = Array.isArray(roleList) ? roleList : [];
      const activeRoles = safeRoleList.filter(r => r && r.Status !== 0);
      setRoles(activeRoles);
      if (!createFields.rbacId && activeRoles.length > 0) {
        setCreateFields(prev => ({ ...prev, rbacId: activeRoles[0].RbacId }));
      }
    } catch {
      showError('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, []); // eslint-disable-line

  // ── Edit existing user ──────────────────────────────────────────────────
  const openEdit = (user) => {
    setEditUser(user);
    setEditFields({
      rbacId: user.rbacId || roles[0]?.RbacId || '',
      active: user.assocStatus === 1,
    });
  };

  const handleSave = async () => {
    if (!editFields.rbacId) {
      showError('Role is required');
      return;
    }
    setSaving(true);
    try {
      await updateUser(editUser.UserId, editFields);
      showSuccess('User updated successfully');
      setEditUser(null);
      reload();
    } catch (e) {
      showError(e?.response?.data?.message || 'Failed to update user');
    } finally {
      setSaving(false);
    }
  };

  // ── Create new user ─────────────────────────────────────────────────────
  const handleCreate = async () => {
    if (!createFields.name || !createFields.email) {
      showError('Name and Email are required');
      return;
    }
    if (!createFields.rbacId) {
      showError('Role is required');
      return;
    }
    setCreating(true);
    try {
      const created = await createUser(createFields);
      showSuccess('User created successfully');
      if (created?.UserId) {
        setUsers((prev) => [
          {
            UserId: created.UserId,
            Email: created.Email || createFields.email,
            Name: created.Name || createFields.name,
            roleName: roles.find((r) => r.RbacId === createFields.rbacId)?.RoleName || null,
            rbacId: createFields.rbacId,
            assocStatus: 1,
            assocAccId: created.AccountId || null,
            assocAppId: created.ApplicationId || null,
          },
          ...prev,
        ]);
      }
      setShowCreate(false);
      setCreateFields({
        name: '',
        email: '',
        phone: '',
        rbacId: roles[0]?.RbacId || '',
      });
      reload();
    } catch (e) {
      showError(e?.response?.data?.message || 'Failed to create user');
    } finally {
      setCreating(false);
    }
  };

  // ── Filter ──────────────────────────────────────────────────────────────
  const visible = users.filter(u => {
    const matchSearch = !search ||
      u.Email?.toLowerCase().includes(search.toLowerCase()) ||
      u.Name?.toLowerCase().includes(search.toLowerCase());
    const matchRole = !roleFilter || u.rbacId === roleFilter;
    const status = String(u.assocStatus ?? u.Status ?? '');
    const matchStatus = !statusFilter || status === statusFilter;
    return matchSearch && matchRole && matchStatus;
  });

  return (
    <div className="user-management">
      <div className="user-mgmt-header">
        <h2>🔐 User Management</h2>
        <button className="btn-create-user" onClick={() => setShowCreate(true)}>+ New User</button>
      </div>

      <div className="user-mgmt-controls">
        <input
          type="text"
          placeholder="Search by email or name…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <select value={roleFilter} onChange={e => setRoleFilter(e.target.value)}>
          <option value="">All Roles</option>
          {roles.map(r => (
            <option key={r.RbacId} value={r.RbacId}>{r.RoleName}</option>
          ))}
        </select>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
          <option value="">All Status</option>
          <option value="1">Active</option>
          <option value="0">Inactive</option>
        </select>
        <span className="user-count">{visible.length} user{visible.length !== 1 ? 's' : ''}</span>
      </div>

      {loading ? (
        <div className="user-table-loading">Loading users…</div>
      ) : (
        <table className="user-table">
          <thead>
            <tr>
              <th>Email</th>
              <th>Name</th>
              <th>Role</th>
              <th>Status</th>
              <th>Account</th>
              <th>Application</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {visible.length === 0 ? (
              <tr>
                <td colSpan={7} style={{ textAlign: 'center', padding: 32, color: '#9ca3af' }}>
                  No users found
                </td>
              </tr>
            ) : visible.map(user => {
              const meta = { label: user.roleName || 'Unassigned', icon: '👤', css: '' };
              const isActive = user.assocStatus === 1;
              return (
                <tr key={user.UserId}>
                  <td>{user.Email}</td>
                  <td>{user.Name || <span style={{ color: '#9ca3af' }}>—</span>}</td>
                  <td>
                    <span className={`role-badge ${meta.css}`}>
                      {meta.icon} {meta.label}
                    </span>
                  </td>
                  <td>
                    <span className={isActive ? 'status-active' : 'status-inactive'}>
                      {isActive ? '● Active' : '○ Inactive'}
                    </span>
                  </td>
                  <td>{user.assocAccId || '—'}</td>
                  <td>{user.assocAppId || '—'}</td>
                  <td>
                    <button className="btn-edit-user" onClick={() => openEdit(user)}>
                      Edit
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {/* Edit modal */}
      {editUser && (
        <div className="modal-overlay" onClick={() => setEditUser(null)}>
          <div className="modal-box" onClick={e => e.stopPropagation()}>
            <h3>Edit User</h3>
            <p className="modal-email">{editUser.Email}</p>

            <div className="modal-field">
              <label>Role</label>
              <select
                value={editFields.rbacId}
                onChange={e => setEditFields(prev => ({ ...prev, rbacId: e.target.value }))}
              >
                {roles.map(r => (
                  <option key={r.RbacId} value={r.RbacId}>{r.RoleName}</option>
                ))}
              </select>
            </div>

            <div className="modal-field">
              <label>Status</label>
              <select
                value={editFields.active ? 'active' : 'inactive'}
                onChange={e => setEditFields(prev => ({ ...prev, active: e.target.value === 'active' }))}
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>

            <div className="modal-actions">
              <button className="btn-cancel" onClick={() => setEditUser(null)}>Cancel</button>
              <button className="btn-save" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving…' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create user modal */}
      {showCreate && (
        <div className="modal-overlay" onClick={() => setShowCreate(false)}>
          <div className="modal-box" onClick={e => e.stopPropagation()}>
            <h3>New User</h3>

            <div className="modal-field">
              <label>Full Name *</label>
              <input
                type="text"
                value={createFields.name}
                onChange={e => setCreateFields(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Full name"
              />
            </div>

            <div className="modal-field">
              <label>Email *</label>
              <input
                type="email"
                value={createFields.email}
                onChange={e => setCreateFields(prev => ({ ...prev, email: e.target.value }))}
                placeholder="user@example.com"
              />
            </div>

            <div className="modal-field">
              <label>Phone</label>
              <input
                type="text"
                value={createFields.phone}
                onChange={e => setCreateFields(prev => ({ ...prev, phone: e.target.value }))}
                placeholder="+49…"
              />
            </div>

            <div className="modal-field">
              <label>Role</label>
              <select
                value={createFields.rbacId}
                onChange={e => setCreateFields(prev => ({ ...prev, rbacId: e.target.value }))}
              >
                <option value="">— select role —</option>
                {roles.map(r => (
                  <option key={r.RbacId} value={r.RbacId}>{r.RoleName}</option>
                ))}
              </select>
            </div>

            <div className="modal-actions">
              <button className="btn-cancel" onClick={() => setShowCreate(false)}>Cancel</button>
              <button className="btn-save" onClick={handleCreate} disabled={creating}>
                {creating ? 'Creating…' : 'Create User'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default UserManagement;
